import React, { Component } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import moment from "moment";
import styled, { css } from "styled-components";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  NotificationsNone as NotificationsNoneIcon,
  NotificationImportant as NotificationImportantIcon,
  Class as ClassIcon,
  Mail as MailIcon,
  ErrorOutline as SupportIcon,
  AssignmentLate as AckIcon
} from "@material-ui/icons";
import { withTranslation } from "react-i18next";

import NotificationService from "../../services/NotificationService";
import markAsRead from "../../utils/markAsRead";
import {
  loadNotifications,
  loadNotificationsCount,
  setNotificationsPage,
  setNotificationsUnread
} from "../../redux/actions";
import { notificationStateType } from "../../types";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

const MAX_PREVIEW = 8;

const BellButton = styled(IconButton)`
  && {
    color: #00303b;
    padding: 10px;
  }

  && svg {
    font-size: 28px;
  }
`;

const priorityStyles = {
  ack: css`
    background: rgba(237, 108, 2, 0.08);
    box-shadow: inset 3px 0 0 0 #ed6c02;

    &:hover {
      background: rgba(237, 108, 2, 0.14);
    }
  `
};

const ListItem = styled(Box)`
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: rgba(0, 48, 59, 0.05);
  }

  &[data-unread="true"] {
    background: ${({ theme }) => theme.color.primary.light};
  }

  &[data-unread="true"]:hover {
    background: ${({ theme }) => theme.color.primary.light};
  }

  ${({ $priority }) =>
    priorityStyles[$priority] ? priorityStyles[$priority] : ""}
`;

const SectionLabel = styled(Typography)`
  && {
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.6px;
    font-weight: 700;
    padding: 10px 14px 4px;
    color: #607681;
  }
`;

function iconForType(type) {
  if (type === "Communication") return <MailIcon fontSize="small" />;
  if (type === "Class") return <ClassIcon fontSize="small" />;
  if (type === "Support") return <SupportIcon fontSize="small" />;
  return <NotificationImportantIcon fontSize="small" />;
}

function iconColor(priority, opened) {
  if (priority === "ack") return "#ed6c02";
  if (!opened) return appTheme.color.primary.main;
  return "#607681";
}

function renderPriorityIcon(priority, type) {
  if (priority === "ack") return <AckIcon fontSize="small" />;
  return iconForType(type);
}

/** Deriva a prioridade da notificação: "ack" (requer ciência) | null. */
function getPriority(n) {
  if (n.ackRequired) return "ack";
  return null;
}

/** Peso para ordenação: quanto menor, mais alto. */
function weight(n) {
  if (getPriority(n) === "ack") return 0;
  if (!n.opened) return 1;
  return 2;
}

function sortForBell(list) {
  return [...list].sort((a, b) => {
    const wa = weight(a);
    const wb = weight(b);
    if (wa !== wb) return wa - wb;
    const ta = new Date(a.createdAt).getTime() || 0;
    const tb = new Date(b.createdAt).getTime() || 0;
    return tb - ta;
  });
}

class NotificationBell extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      loading: false
    };
  }

  componentDidMount() {
    const { notificationState } = this.props;
    if (notificationState.notifications.length === 0) {
      this.loadInitial();
    }
  }

  handleOpen = async event => {
    const { notificationState } = this.props;
    this.setState({ anchorEl: event.currentTarget });
    if (notificationState.notifications.length === 0) {
      await this.loadInitial();
    }
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  loadInitial = async () => {
    const {
      loadNotifications: onLoadNotifications,
      loadNotificationsCount: onLoadCount,
      setNotificationsPage: setPage
    } = this.props;
    this.setState({ loading: true });
    try {
      setPage(0);
      const list = await NotificationService.getNotifications(0);
      onLoadNotifications(list);
      setPage(1);
      const res = await NotificationService.getNotificationCount();
      onLoadCount(res.data);
    } finally {
      this.setState({ loading: false });
    }
  };

  navigateFromNotification = notification => {
    const { history } = this.props;
    const { action } = notification;
    if (action && action.openCommunicationId) {
      history.push(`/communication/${action.openCommunicationId}`);
      return;
    }
    if (notification.type === "Communication") {
      history.push(`/communication/${notification.modelId}`);
      return;
    }
    history.push("/notifications");
  };

  handleClickNotification = async notification => {
    if (!notification.opened) {
      const res = await NotificationService.markAsRead(notification.id);
      markAsRead(res.data);
    }
    this.handleClose();
    this.navigateFromNotification(notification);
  };

  handleMarkAllAsRead = async () => {
    const { notificationState } = this.props;
    const unread = notificationState.notifications.filter(n => !n.opened);
    for (let i = 0; i < unread.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      const res = await NotificationService.markAsRead(unread[i].id);
      markAsRead(res.data);
    }
  };

  handleSeeAll = () => {
    const { history } = this.props;
    this.handleClose();
    history.push("/notifications");
  };

  renderItem = n => {
    const { t } = this.props;
    const priority = getPriority(n);
    return (
      <React.Fragment key={n.id}>
        <ListItem
          display="flex"
          alignItems="flex-start"
          style={{ gap: 10 }}
          data-unread={!n.opened ? "true" : "false"}
          $priority={priority}
          onClick={() => this.handleClickNotification(n)}
        >
          <Box
            flexShrink={0}
            style={{
              color: iconColor(priority, n.opened),
              marginTop: 2
            }}
          >
            {renderPriorityIcon(priority, n.type)}
          </Box>
          <Box flex={1} minWidth={0}>
            {priority ? (
              <Box mb={0.5}>
                <Chip
                  size="small"
                  label={t("ui.notifications.needsAck")}
                  style={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    background: "#ed6c02",
                    color: "#fff"
                  }}
                />
              </Box>
            ) : null}
            <Typography
              variant="body2"
              style={{
                fontWeight: !n.opened ? 600 : 400,
                color: "#00303b",
                lineHeight: 1.35
              }}
            >
              {n.title}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              style={{ marginTop: 2 }}
            >
              {moment(n.createdAt).fromNow()}
            </Typography>
          </Box>
        </ListItem>
        <Divider />
      </React.Fragment>
    );
  };

  render() {
    const { anchorEl, loading } = this.state;
    const { notificationState, t } = this.props;
    const { notifications, unread } = notificationState;
    const open = Boolean(anchorEl);

    const sorted = sortForBell(notifications).slice(0, MAX_PREVIEW);
    const priorityItems = sorted.filter(n => getPriority(n));
    const regularItems = sorted.filter(n => !getPriority(n));

    return (
      <>
        <Tooltip title={t("ui.notifications.title")}>
          <BellButton
            aria-label={t("ui.notifications.open")}
            onClick={this.handleOpen}
          >
            <Badge
              badgeContent={unread}
              color="error"
              overlap="circle"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <NotificationsNoneIcon />
            </Badge>
          </BellButton>
        </Tooltip>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={this.handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            style: {
              width: 400,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "min(70vh, 560px)",
              display: "flex",
              flexDirection: "column"
            }
          }}
        >
          <Box
            px={2}
            py={1.5}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ gap: 8 }}
          >
            <Typography
              variant="subtitle1"
              component="span"
              style={{ fontWeight: 600, color: "#00303b" }}
            >
              {t("ui.notifications.title")}
            </Typography>
            {unread > 0 ? (
              <Button
                size="small"
                color="primary"
                onClick={this.handleMarkAllAsRead}
              >
                {t("ui.notifications.markAllRead")}
              </Button>
            ) : null}
          </Box>
          <Divider />

          <Box style={{ overflowY: "auto", flex: "1 1 auto" }}>
            {loading && notifications.length === 0 ? (
              <Box p={4} textAlign="center">
                <CircularProgress size={20} />
              </Box>
            ) : null}

            {!loading && sorted.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {t("ui.notifications.empty")}
                </Typography>
              </Box>
            ) : null}

            {priorityItems.length > 0 ? (
              <>
                <SectionLabel component="div">
                  {t("ui.notifications.priority")}
                </SectionLabel>
                {priorityItems.map(this.renderItem)}
              </>
            ) : null}

            {regularItems.length > 0 ? (
              <>
                {priorityItems.length > 0 ? (
                  <SectionLabel component="div">
                    {t("ui.notifications.recent")}
                  </SectionLabel>
                ) : null}
                {regularItems.map(this.renderItem)}
              </>
            ) : null}
          </Box>

          <Box
            px={2}
            py={1}
            display="flex"
            justifyContent="flex-end"
            style={{ borderTop: "1px solid rgba(0,48,59,0.08)" }}
          >
            <Button size="small" color="primary" onClick={this.handleSeeAll}>
              {t("ui.notifications.seeAll")}
            </Button>
          </Box>
        </Popover>
      </>
    );
  }
}

NotificationBell.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  notificationState: notificationStateType.isRequired,
  loadNotifications: PropTypes.func.isRequired,
  loadNotificationsCount: PropTypes.func.isRequired,
  setNotificationsPage: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  setNotificationsUnread: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  notificationState: state.notificationState
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loadNotifications,
      loadNotificationsCount,
      setNotificationsPage,
      setNotificationsUnread
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withTranslation()(NotificationBell)));
